import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Layout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TimesheetPage from "./pages/TimesheetPage";
import SummaryPage from "./pages/SummaryPage";
import ReviewPage from "./pages/ReviewPage";
import UsersPage from "./pages/UsersPage";
import ProjectsPage from "./pages/ProjectsPage";
import AssignmentsPage from "./pages/AssignmentsPage";
import ReportsPage from "./pages/ReportsPage";
import FinancialReportPage from "./pages/FinancialReportPage";
import ClientsPage from "./pages/ClientsPage";
import EmployeeSchedulesPage from "./pages/EmployeeSchedulesPage";
import PlanningPage from "./pages/PlanningPage";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import ClientSettingsPage from "./pages/ClientSettingsPage";

// Loading spinner
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Smart redirect based on role
const SmartRedirect = () => {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isClientManagerOnly = roles.includes('CLIENT_MANAGER') &&
    !roles.includes('ADMIN') && !roles.includes('MANAGER') && !roles.includes('EMPLOYEE');
  return <Navigate to={isClientManagerOnly ? '/client-dashboard' : '/timesheet'} replace />;
};

// Protected route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles) {
    const userRoles = user.roles || [user.role];
    const hasRequiredRole = allowedRoles.some(role => userRoles.includes(role));
    if (!hasRequiredRole) {
      // CLIENT_MANAGER shouldn't land on /timesheet — send to their dashboard
      const isClientManagerOnly = userRoles.includes('CLIENT_MANAGER') &&
        !userRoles.includes('ADMIN') && !userRoles.includes('MANAGER') && !userRoles.includes('EMPLOYEE');
      return <Navigate to={isClientManagerOnly ? '/client-dashboard' : '/timesheet'} replace />;
    }
  }

  return <Layout>{children}</Layout>;
};

// Public route wrapper (redirect to app if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    const roles = user.roles || [user.role];
    const isClientManagerOnly = roles.includes('CLIENT_MANAGER') &&
      !roles.includes('ADMIN') && !roles.includes('MANAGER') && !roles.includes('EMPLOYEE');
    return <Navigate to={isClientManagerOnly ? '/client-dashboard' : '/timesheet'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Protected routes - All authenticated users */}
      <Route
        path="/timesheet"
        element={
          <ProtectedRoute>
            <TimesheetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/summary"
        element={
          <ProtectedRoute>
            <SummaryPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes - Admin & Manager only */}
      <Route
        path="/review"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "MANAGER"]}>
            <ReviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assignments"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "MANAGER"]}>
            <AssignmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "MANAGER", "CLIENT_MANAGER"]}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes - Admin only */}
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["ADMIN"]}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute allowedRoles={["ADMIN"]}>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute allowedRoles={["ADMIN"]}>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedules"
        element={
          <ProtectedRoute allowedRoles={["ADMIN"]}>
            <EmployeeSchedulesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financials"
        element={
          <ProtectedRoute allowedRoles={["ADMIN"]}>
            <FinancialReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "MANAGER", "EMPLOYEE"]}>
            <PlanningPage />
          </ProtectedRoute>
        }
      />

      {/* CLIENT_MANAGER exclusive routes */}
      <Route
        path="/client-dashboard"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "CLIENT_MANAGER"]}>
            <ClientDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-settings"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "CLIENT_MANAGER"]}>
            <ClientSettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Redirect root based on role */}
      <Route path="/" element={<SmartRedirect />} />

      {/* Catch all */}
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
